/**
 * AddVendorLineModal (S1.4) — add a multi-vendor sourcing line. The mock
 * handler enforces the exactly-one-primary invariant (first line auto-primary;
 * an explicit primary flag is exclusive).
 */

import { useEffect, useMemo } from 'react';
import { Col, Input, InputNumber, Modal, Radio, Row, Select, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import { useVendorOptions } from '@/hooks/useVendors';
import { UNIT_VALUES } from '@/schemas/forms/productFormSchemas';
import type { Product } from '@/types/api/product';
import { addVendorLineSchema } from '../schemas';
import type { AddVendorLineFormData } from '../schemas';

const { Text } = Typography;

const CURRENCY_VALUES = ['AUD', 'USD', 'TWD', 'CNY', 'EUR'] as const;

interface AddVendorLineModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
}

export const AddVendorLineModal = ({
  open,
  product,
  onCancel,
  onSaved,
}: AddVendorLineModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { addVendorLine } = useProductMutations(product.id);

  const { vendors, isLoading: vendorsLoading } = useVendorOptions({ ready: open });

  const linkedVendorIds = useMemo(
    () => new Set((product.vendorLines ?? []).map((line) => line.vendorId)),
    [product.vendorLines]
  );

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((vendor) => !linkedVendorIds.has(vendor.vendorId))
        .map((vendor) => ({ value: vendor.vendorId, label: vendor.vendorName })),
    [vendors, linkedVendorIds]
  );

  const defaultFormValues = useMemo<AddVendorLineFormData>(
    () => ({
      vendorId: 0,
      vendorSku: '',
      unitCost: 0,
      currency: 'USD',
      orderUnit: undefined,
      isPrimary: (product.vendorLines ?? []).length === 0,
    }),
    [product.vendorLines]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<AddVendorLineFormData>({
    resolver: zodResolver(addVendorLineSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(defaultFormValues);
  }, [open, defaultFormValues, reset]);

  const onSubmit = async (data: AddVendorLineFormData) => {
    const vendor = vendors.find((item) => item.vendorId === data.vendorId);
    try {
      await addVendorLine({
        vendorId: data.vendorId,
        vendorName: vendor?.vendorName ?? t('products:vendorFallback', { id: data.vendorId }),
        vendorSku: data.vendorSku || undefined,
        unitCost: data.unitCost,
        currency: data.currency,
        orderUnit: data.orderUnit || undefined,
        isPreferred: data.isPrimary,
      });
      handleSuccess(t('products:messages.vendorAdded'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  return (
    <Modal
      title={t('products:addVendor')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:wizard.sections.vendorLines')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {requiredMark}
                {t('products:fields.vendor')}
              </Text>
              <Controller
                name="vendorId"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      value={field.value || undefined}
                      showSearch
                      loading={vendorsLoading}
                      optionFilterProp="label"
                      size="large"
                      options={vendorOptions}
                      status={errors.vendorId && touchedFields.vendorId ? 'error' : undefined}
                      style={formStyles.inputStyle}
                      onChange={(value) => {
                        field.onChange(value ?? 0);
                        trigger('vendorId');
                      }}
                    />
                    {errors.vendorId && touchedFields.vendorId && (
                      <Text style={formStyles.errorStyle}>{t(errors.vendorId.message ?? '')}</Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>{t('products:fields.vendorSku')}</Text>
              <Controller
                name="vendorSku"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" style={formStyles.inputStyle} />
                )}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                {requiredMark}
                {t('products:fields.unitCost')}
              </Text>
              <Controller
                name="unitCost"
                control={control}
                render={({ field }) => (
                  <>
                    <InputNumber
                      value={field.value ?? null}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('unitCost');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      status={errors.unitCost && touchedFields.unitCost ? 'error' : undefined}
                      style={formStyles.inputStyle}
                    />
                    {errors.unitCost && touchedFields.unitCost && (
                      <Text style={formStyles.errorStyle}>{t(errors.unitCost.message ?? '')}</Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>{t('products:fields.currency')}</Text>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="large"
                    options={CURRENCY_VALUES.map((code) => ({ value: code, label: code }))}
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
            <Col span={8}>
              {/* W7-B3 訂購單位 — optional capture; NO qty conversion (Q5). */}
              <Text style={formStyles.labelStyle}>{t('products:fields.orderUnit')}</Text>
              <Controller
                name="orderUnit"
                control={control}
                render={({ field }) => (
                  <Select
                    size="large"
                    allowClear
                    options={UNIT_VALUES.map((unit) => ({ value: unit, label: unit }))}
                    value={field.value || undefined}
                    style={formStyles.inputStyle}
                    onChange={(value) => field.onChange(value ?? undefined)}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>{t('products:fields.preferred')}</Text>
              <Controller
                name="isPrimary"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    style={{
                      display: 'flex',
                      height: `${controlHeightTokens.controlHeightXL}px`,
                      alignItems: 'center',
                    }}
                  >
                    <Radio value={true}>{t('products:preferred')}</Radio>
                    <Radio value={false}>{t('products:notPreferred')}</Radio>
                  </Radio.Group>
                )}
              />
            </Col>
          </Row>
        </div>
      </form>
    </Modal>
  );
};

export default AddVendorLineModal;
